import React, { useState } from 'react';
/**
 * Governance Rules — Sub-components
 *
 * RuleCard and ThresholdSection used by GovernanceRulesTab.
 *
 * Phase 3 cleanup: legacy RulebookCard (static markdown metadata) removed —
 * the Regelboeken tab now renders LiveRulebookCard from the executable
 * RULEBOOKS_REGISTRY.
 *
 * Canonical location: portals/admin/content/governance/ (DDD — UI in portal)
 */
import { XCircleIcon, AlertTriangleIcon, AlertOctagonIcon, EyeIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { DEFAULT_POLICY } from '../../../../governance';
import type { Severity, Stage, Rule } from './governanceRulesData';
import { STAGE_LABELS, STAGE_COLORS } from './governanceRulesData';
// ─── Severity Config ──────────────────────────────────────────
// Phase 1 (Strak-Trek): Extended to 5 levels. Existing rules with severity
// 'WARNING' continue to render as the coach/verbeterpunt style (now blue,
// aligned with shared.tsx SEVERITY_CONFIG.coach).
const SEVERITY_CONFIG: Record<Severity, {
  icon: React.ReactNode;
  bg: string;
  text: string;
  border: string;
  label: string;
}> = {
  CRITICAL: {
    icon: <AlertOctagonIcon className="h-4 w-4" data-id="element-4340" data-mp="3gxydhjvovbmm" />,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Kritiek'
  },
  BLOCKER: {
    icon: <XCircleIcon className="h-4 w-4" data-id="element-4341" data-mp="3736ag0jc7wkg" />,
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    label: 'Blokkerend'
  },
  REVIEW: {
    icon: <EyeIcon className="h-4 w-4" data-id="element-4342" data-mp="3kv75oa23mh7" />,
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    label: 'Review'
  },
  WARNING: {
    icon: <AlertTriangleIcon className="h-4 w-4" data-id="element-4343" data-mp="3qpulpnrhoi4k" />,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Verbeterpunt'
  },
  INFO: {
    icon: <InfoIcon className="h-4 w-4" data-id="element-4344" data-mp="2jdluzrme7hfx" />,
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    border: 'border-slate-200',
    label: 'Informatief'
  }
};
// ─── RuleCard ─────────────────────────────────────────────────
export function RuleCard({
  rule
}: {
  rule: Rule;
}) {
  const [expanded, setExpanded] = useState(false);
  // Defensive: fall back to INFO styling if a rule carries an unregistered
  // severity label (prevents a single bad data row from crashing the tab).
  const sev = SEVERITY_CONFIG[rule.severity] ?? SEVERITY_CONFIG.INFO;
  return <div className={`rounded-lg border ${sev.border} bg-white overflow-hidden`} data-id="element-4345" data-mp="1y13de11od39x">
      <button className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-gray-50" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} data-id="element-4346" data-mp="22rtj5fe0j855">
        <div className={`mt-0.5 flex-shrink-0 ${sev.text}`} data-id="element-4347" data-mp="1t20gx4zrg5dk">{sev.icon}</div>
        <div className="min-w-0 flex-1" data-id="element-4348" data-mp="1szxipxogf5xr">
          <div className="flex flex-wrap items-center gap-2" data-id="element-4349" data-mp="1f28yms27o2tr">
            <code className="text-xs font-mono text-gray-400" data-id="element-4350" data-mp="1j42w6rh40qpp">{rule.id}</code>
            <span className="font-semibold text-gray-800" data-id="element-4351" data-mp="1j3b2xnazj52l">{rule.label}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_COLORS[rule.stage]}`} data-id="element-4352" data-mp="10gldb3k9us9b">
              {STAGE_LABELS[rule.stage]}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${sev.bg} ${sev.text}`} data-id="element-4353" data-mp="19h9rby9o0sm7">
              {sev.label}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-600" data-id="element-4354" data-mp="1jdmiophqmuds">{rule.description}</p>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-4355" data-mp="1eg339m9b0bk9">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-4356" data-mp="1s6q5hoe9wjl3" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-4357" data-mp="1s66pid86o59p" />}
        </div>
      </button>

      {expanded && <div className={`border-t ${sev.border} ${sev.bg} px-4 py-3 space-y-3`} data-id="element-4358" data-mp="1o7qsrhmavij3">
          {rule.example && <div data-id="element-4359" data-mp="1x62nnvu792hc">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1" data-id="element-4360" data-mp="1x6ksvhyuar1e">
                Bron / Voorbeeld
              </p>
              <code className="block rounded bg-white/70 px-3 py-2 text-xs text-gray-700 border border-gray-200" data-id="element-4361" data-mp="2h7xaly7ppw0s">
                {rule.example}
              </code>
            </div>}
          {rule.fix && <div data-id="element-4362" data-mp="2q2ttjih8in1u">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1" data-id="element-4363" data-mp="2uzar13abqdrl">
                Oplossing
              </p>
              <p className="text-sm text-gray-700" data-id="element-4364" data-mp="1yamkp47z0ey0">{rule.fix}</p>
            </div>}
        </div>}
    </div>;
}
// ─── ThresholdSection ─────────────────────────────────────────
export function ThresholdSection() {
  const gates = DEFAULT_POLICY.gates;
  const budgets = DEFAULT_POLICY.contentBudgets;
  const flags = DEFAULT_POLICY.featureFlags;
  const thresholdItems = [{
    label: 'Productie min. score',
    value: `${gates.productionMinScore}%`,
    description: 'Vragen onder deze score worden geblokkeerd voor productie (oefenmodus).',
    path: 'gates.productionMinScore',
    color: gates.productionMinScore >= 70 ? 'text-emerald-700' : 'text-amber-700'
  }, {
    label: 'Assessment min. score',
    value: `${gates.assessmentMinScore}%`,
    description: 'Vragen onder deze score zijn niet geschikt voor CITO-stijl toetsen.',
    path: 'gates.assessmentMinScore',
    color: gates.assessmentMinScore >= 80 ? 'text-emerald-700' : 'text-amber-700'
  }, {
    label: 'Review drempel (coach ernst)',
    value: `≥ ${gates.reviewCoachSeverityThreshold}`,
    description: 'Coach-issues met ernst ≥ deze waarde triggeren een review-markering.',
    path: 'gates.reviewCoachSeverityThreshold',
    color: 'text-blue-700'
  }, {
    label: 'Min. vragen per family (assessment)',
    value: `${gates.assessmentMinQuestionsPerFamily}`,
    description: 'Families met minder vragen zijn niet assessment-geschikt.',
    path: 'gates.assessmentMinQuestionsPerFamily',
    color: 'text-blue-700'
  }];
  const budgetItems = [{
    label: 'Min. uitleglengte (basic)',
    value: `${budgets.minExplanationLengthByTierBand.basic} tekens`,
    description: 'Tiers 1–4 (B6/M6)',
    path: 'contentBudgets.minExplanationLengthByTierBand.basic'
  }, {
    label: 'Min. uitleglengte (intermediate)',
    value: `${budgets.minExplanationLengthByTierBand.intermediate} tekens`,
    description: 'Tiers 5–8 (E6/B7)',
    path: 'contentBudgets.minExplanationLengthByTierBand.intermediate'
  }, {
    label: 'Min. uitleglengte (advanced)',
    value: `${budgets.minExplanationLengthByTierBand.advanced} tekens`,
    description: 'Tiers 9–12 (M7/E7)',
    path: 'contentBudgets.minExplanationLengthByTierBand.advanced'
  }, {
    label: 'Min. uitleglengte (expert)',
    value: `${budgets.minExplanationLengthByTierBand.expert} tekens`,
    description: 'Tiers 13–16 (B8/E8+)',
    path: 'contentBudgets.minExplanationLengthByTierBand.expert'
  }, {
    label: 'Min. distractor-uitleg (basic)',
    value: `${budgets.minDistractorLengthByTierBand.basic} tekens`,
    description: 'Tiers 1–4',
    path: 'contentBudgets.minDistractorLengthByTierBand.basic'
  }, {
    label: 'Min. distractor-uitleg (intermediate)',
    value: `${budgets.minDistractorLengthByTierBand.intermediate} tekens`,
    description: 'Tiers 5–8',
    path: 'contentBudgets.minDistractorLengthByTierBand.intermediate'
  }];
  const featureFlagItems = Object.entries(flags).map(([key, value]) => ({
    key,
    value: value as boolean,
    label: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
  }));
  return <div className="space-y-6" data-id="element-4365" data-mp="2k74e50wj05bk">
      {/* Info banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-id="element-4366" data-mp="1wilc3342korx">
        <div className="flex items-start gap-3" data-id="element-4367" data-mp="1rk1mmsq9ouut">
          <InfoIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-4368" data-mp="1rj8ouh9adfot" />
          <div data-id="element-4369" data-mp="161i9uszg5f5t">
            <p className="text-sm font-semibold text-blue-800" data-id="element-4370" data-mp="1620eacw9izot">
              Drempelwaarden aanpassen
            </p>
            <p className="text-sm text-blue-700 mt-0.5" data-id="element-4371" data-mp="1b01wkvlv6uz8">
              Drempelwaarden zijn gedefinieerd in{' '}
              <code className="font-mono bg-blue-100 px-1 rounded" data-id="element-4372" data-mp="1g0zer86vjp0q">
                src/governance/pipeline/defaultPolicy.ts
              </code>
              . Pas het bestand direct aan om drempelwaarden te wijzigen.
              Gebruik de <strong data-id="element-4373" data-mp="1tye7omwc6exg">Instellingen</strong> tab voor
              runtime-aanpassingen.
            </p>
          </div>
        </div>
      </div>

      {/* Gate Thresholds */}
      <div data-id="element-4374" data-mp="1yrqi9t46kl77">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-4375" data-mp="23oh8385sev5t">
          Kwaliteitspoorten
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4376" data-mp="27qu4h5yn9gpx">
          {thresholdItems.map((item, i) => <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i < thresholdItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-4377" data-mp="2cpf3j225tucs" data-mpi={i}>
              <div className="min-w-0" data-id="element-4378" data-mp="3s6juqz1tk160">
                <p className="text-sm font-medium text-gray-800" data-id="element-4379" data-mp="3o688xixpzusy">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-4380" data-mp="3j7yg3ezgx6ai">
                  {item.description}
                </p>
                <code className="text-xs text-gray-400 font-mono mt-1 block" data-id="element-4381" data-mp="jr8gbxgtzy4s">
                  {item.path}
                </code>
              </div>
              <span className={`text-lg font-bold flex-shrink-0 ${item.color}`} data-id="element-4382" data-mp="ft2ft76l6esb">
                {item.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Content Budgets */}
      <div data-id="element-4383" data-mp="13g0rlrjrgyjw">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-4384" data-mp="13gsjose5ge5f">
          Inhoudsbudgetten
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4385" data-mp="yosiyxq99h7s">
          {budgetItems.map((item, i) => <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i < budgetItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-4386" data-mp="3vp1hemumxuga" data-mpi={i}>
              <div className="min-w-0" data-id="element-4387" data-mp="8ge9ppbo6eeq">
                <p className="text-sm font-medium text-gray-800" data-id="element-4388" data-mp="4gduw3kns175">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-4389" data-mp="ifn0687btawc">
                  {item.description}
                </p>
                <code className="text-xs text-gray-400 font-mono mt-1 block" data-id="element-4390" data-mp="dh28ix653zji">
                  {item.path}
                </code>
              </div>
              <span className="text-sm font-bold text-gray-700 flex-shrink-0" data-id="element-4391" data-mp="n7n6qjhwj21w">
                {item.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Feature Flags */}
      <div data-id="element-4392" data-mp="w8l9yzkin5jf">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-4393" data-mp="w94ixqs6ujlb">
          Feature Flags
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4394" data-mp="1fuekzyp5zsjb">
          {featureFlagItems.map((item, i) => <div key={item.key} className={`flex items-center justify-between gap-4 px-4 py-3 ${i < featureFlagItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-4395" data-mp="16t6uw3wsxy0b" data-mpi={i}>
              <div className="min-w-0" data-id="element-4396" data-mp="x3nct0gwy86i">
                <p className="text-sm font-medium text-gray-800" data-id="element-4397" data-mp="x2lp9wipnt7w">
                  {item.label}
                </p>
                <code className="text-xs text-gray-400 font-mono" data-id="element-4398" data-mp="11vesgfyj6thv">
                  featureFlags.{item.key}
                </code>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold flex-shrink-0 ${item.value ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-4399" data-mp="s3r8t187ugqy">
                {item.value ? 'Actief' : 'Inactief'}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}